import * as React from 'react';
import { FormControl, InputLabel, MenuItem, Select } from "@mui/material";
import { Control, FieldValues, useController } from "react-hook-form";

interface Option {
  label: string;
  value: string;
}

interface SelectFieldProps<T extends FieldValues> {
  name: keyof T;
  control: Control<T>;
  label?: string;
  options: Option[];
  required?: boolean;
  defaultValue: any;
}

export function SelectField<T extends FieldValues>({
  name,
  control,
  label = undefined,
  options,
  required = false,
  defaultValue = null,
}: SelectFieldProps<T>) {
  const {
    field: { value, onChange },
    fieldState: { error },
  } = useController<T>({
    name: name as string,
    control,
    defaultValue: defaultValue ? defaultValue : null,
  });
  
  const rawValue = React.useMemo(() => {
    if (value && typeof value === "object" && "@id" in value) {
      return (value as Record<string, any>)["@id"];
    }
    return value as string | null;
  }, [value]);

  const selectedValue = React.useMemo(
    () => (options.some(o => o.value === rawValue) ? rawValue : null),
    [rawValue, options]
  );

  const handleChange = (evt: React.ChangeEvent<{ value: unknown }>) => {
    const next = evt.target.value as string;
    onChange(next === "" ? null : next);
  };

  if (!options?.length) {
    return null;
  }

  return (
    <FormControl size="small" fullWidth error={!!error}>
      <InputLabel required={required}>
        {label}
      </InputLabel>

      <Select
        label={label ?? defaultValue.label}
        required={required}
        value={selectedValue ?? defaultValue?.value ?? null}
        onChange={handleChange}
      >
        <MenuItem value="">
          <em>Select…</em>
        </MenuItem>

        {options.map(opt => (
          <MenuItem key={opt.value} value={opt.value}>
            {opt.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}